import { useCallback, useRef, useState, useEffect } from "react";
import { debounce } from "lodash";

type Dimensions = {
  width?: number | string;
  height?: number | string;
};

const INTERVAL = 300;

export const useMeasure = ({ disableOnCondition = false }: { disableOnCondition?: boolean } = {}): [
  (node: HTMLElement | null) => void,
  Dimensions
] => {
  const [dimensions, setDimensions] = useState<Dimensions>({});
  const nodeRef = useRef<HTMLElement | null>(null);
  const resizeObserver = useRef<ResizeObserver | null>(null);

  const updateDimensions = useCallback(() => {
    if (disableOnCondition) {
      return;
    }

    const node = nodeRef.current;
    if (!node) {
      return;
    };

    const width = node.offsetWidth;
    const height = node.offsetHeight;

    setDimensions(prev => {
      if (prev.width !== width || prev.height !== height) {
        return { width, height };
      }
      return prev;
    });
  }, [disableOnCondition]);

  const ref = useCallback(
    (node: HTMLElement | null) => {
      if (resizeObserver.current) {
        resizeObserver.current.disconnect();
        resizeObserver.current = null;
      }

      if (node) {
        nodeRef.current = node;
        updateDimensions();

        const ro = new ResizeObserver(debounce(() => updateDimensions()));
        ro.observe(node);
        resizeObserver.current = ro;
      }
    },
    [updateDimensions]
  );

  useEffect(() => {
    if (disableOnCondition) {
      return;
    }

    const id = setInterval(updateDimensions, INTERVAL);
    return () => clearInterval(id);
  }, [updateDimensions, disableOnCondition]);

  return [ref, { width: dimensions.width || 'auto', height: dimensions.height || 'auto' }];
};
